import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:19000/api/v1';
const TOKEN_KEY = 'nano-stock-token';

export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, string[]>;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add language header for i18n
    const language = localStorage.getItem('nano-stock-language') || 'ar';
    if (config.headers) {
      config.headers['Accept-Language'] = language;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('nano-stock-refresh-token');

      if (refreshToken) {
        try {
          const response = await axios.post<{ accessToken: string }>(
            `${API_BASE_URL}/auth/refresh`,
            { refreshToken }
          );

          const { accessToken } = response.data;
          localStorage.setItem(TOKEN_KEY, accessToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, dispatch session expired event
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem('nano-stock-refresh-token');
          localStorage.removeItem('nano-stock-user');
          // Dispatch custom event for AuthProvider to handle navigation
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token - only dispatch session expired if user was previously authenticated
        // This prevents login redirect loop for unauthenticated users hitting protected endpoints
        const wasAuthenticated = localStorage.getItem(TOKEN_KEY) !== null;
        if (wasAuthenticated) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem('nano-stock-refresh-token');
          localStorage.removeItem('nano-stock-user');
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
      }
    }

    // Transform error for consistent handling
    const apiError: ApiError = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      code: error.response?.data?.code || 'UNKNOWN_ERROR',
      details: error.response?.data?.details,
    };

    return Promise.reject(apiError);
  }
);

// Helper to set auth token
export function setAuthToken(token: string): void {
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}

// Helper to clear auth token
export function clearAuthToken(): void {
  delete api.defaults.headers.common.Authorization;
}

// Type-safe API methods
export const apiClient = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    api.get<T>(url, { params }).then((res) => res.data),

  post: <T>(url: string, data?: unknown) =>
    api.post<T>(url, data).then((res) => res.data),

  put: <T>(url: string, data?: unknown) =>
    api.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) =>
    api.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) =>
    api.delete<T>(url).then((res) => res.data),
};

export default api;
